import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { wrapFormikHandleChange } from "../../../../infrastructure/utils/formatters";

import UserManagementService from "../../../../infrastructure/api/userManagement";
import BasicInformationService from "../../../../infrastructure/api/basicInformation";
import { useApiHandler } from "../../../common/hooks/useApiHandler";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../components/shared/dropdown";
import TabPanel from "../../../components/shared/tab-panel";
import RoleInfoSelect from "../../../components/user-management/RoleInfoSelect";
import {
  normalizeUserKindForForm,
  resolveUserKindFromRoleId,
} from "../../../../infrastructure/utils/userKindUtils";

const validationSchema = Yup.object().shape({
  firstName: Yup.string(),
  lastName: Yup.string(),
  userName: Yup.string().required("لطفا این فیلد را پر کنید."),
  email: Yup.string()
    .email("فرمت ایمیل اشتباه است.")
    .required("لطفا این فیلد را پر کنید."),
  mobile: Yup.string(),
  isActive: Yup.bool(),
  password: Yup.string().required("لطفا این فیلد را پر کنید."),
  confirmPassword: Yup.string()
    .required("لطفا این فیلد را پر کنید.")
    .oneOf([Yup.ref("password"), null], "رمزعبور و تکرار آن همخوانی ندارد."),
});

function a11yProps(index) {
  return {
    id: `userinfo-tab-${index}`,
    "aria-controls": `userinfo-tab-panel-${index}`,
    iconPosition: "start",
  };
}

const UserInfoModifyPage = () => {
  const data = useLoaderData();
  let navigate = useNavigate();
  const { id: userId } = useParams();
  const [tabValue, setTabValue] = React.useState(0);
  const [loading, setLoading] = useState(false);
  const [cityPermission, setCityPermission] = useState(data.cityPermission);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [expandedProvinces, setExpandedProvinces] = useState([]);
  const { executeApiWithSuccess } = useApiHandler();

  // Load province and city metadata for hierarchical city selection
  useEffect(() => {
    const loadBasicInfo = async () => {
      try {
        const [provincesResponse, citiesResponse] = await Promise.all([
          BasicInformationService.getProvinceInformationListApi(),
          BasicInformationService.getCityInformationListApi(),
        ]);

        if (provincesResponse?.isSuccess && provincesResponse.data) {
          setProvinces(
            provincesResponse.data.map((p) => ({
              id: p.id,
              provinceName: p.provinceName,
            }))
          );
        }

        if (citiesResponse?.isSuccess && citiesResponse.data) {
          setCities(
            citiesResponse.data.map((c) => ({
              id: c.id,
              cityName: c.cityName,
              provinceId: c.provinceId,
            }))
          );
        }
      } catch (error) {
        // فقط لاگ کن، نگذار صفحه کرش کند
        // اگر این دیتا نباشد، همچنان ساختار فعلی (لیست ساده شهرها) کار می‌کند.
        // eslint-disable-next-line no-console
        console.error("Error loading provinces/cities for user city permissions", error);
      }
    };

    loadBasicInfo();
  }, []);

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const syncedUserKind =
      resolveUserKindFromRoleId(formValue.roleId, data?.roleList) ||
      normalizeUserKindForForm(formValue.userKind);
    const sendData = {
      ...data,
      userInfo: {
        ...formValue,
        userKind: syncedUserKind || formValue.userKind,
      },
      cityPermission: cityPermission,
    };
    console.log("sendData", sendData);

    try {
      await executeApiWithSuccess(
        () => UserManagementService.modifyUserInfoApi(sendData),
        null, // Let the API response provide the success message
        {
          onSuccess: () => {
            navigate("/UM/Yeksa/userinfo");
          },
          onFinally: () => {
            setLoading(false);
            setSubmitting(false);
          },
        }
      );
    } catch (error) {
      // Error is already handled by executeApiWithSuccess
      // Only need to reset loading states for non-auth errors
      if (!error?.isAuthError) {
        setLoading(false);
        setSubmitting(false);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const onCheckedCityPermission = (event, city) => {
    const checked = event.target.checked ? true : false;

    const newCityPermission = cityPermission.map((x) => {
      if (x.cityId === city.cityId) {
        x.allowed = checked;
      }

      return x;
    });

    setCityPermission(newCityPermission);
  };

  const onHandleAllCheckedCity = (isChecked) => {
    const newCityPermission = cityPermission.map((x) => ({
      ...x,
      allowed: isChecked,
    }));

    setCityPermission(newCityPermission);
  };

  // provinceId-level toggle: select/deselect all cities in a province
  const onToggleProvinceCities = (provinceId, isChecked) => {
    const newCityPermission = cityPermission.map((x) => {
      const meta = cities.find((c) => c.id === x.cityId);
      if (meta && meta.provinceId === provinceId) {
        return { ...x, allowed: isChecked };
      }
      return x;
    });
    setCityPermission(newCityPermission);
  };

  // Build hierarchical structure: provinces -> cities (with current allowed state)
  const provinceGroups = React.useMemo(() => {
    if (!cities.length || !provinces.length || !Array.isArray(cityPermission)) {
      return [];
    }

    const map = new Map();

    cities.forEach((c) => {
      const perm = cityPermission.find((p) => p.cityId === c.id);
      const allowed = perm ? perm.allowed : false;

      let group = map.get(c.provinceId);
      if (!group) {
        const province = provinces.find((p) => p.id === c.provinceId);
        group = {
          provinceId: c.provinceId,
          provinceName: province?.provinceName || `استان ${c.provinceId}`,
          cities: [],
        };
        map.set(c.provinceId, group);
      }

      group.cities.push({
        cityId: c.id,
        cityName: c.cityName,
        allowed,
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.provinceName.localeCompare(b.provinceName, "fa")
    );
  }, [cities, provinces, cityPermission]);

  const isProvinceExpanded = (provinceId) =>
    expandedProvinces.includes(provinceId);

  const toggleProvinceExpanded = (provinceId) => {
    setExpandedProvinces((prev) =>
      prev.includes(provinceId)
        ? prev.filter((id) => id !== provinceId)
        : [...prev, provinceId]
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              userId !== "create"
                ? `ویرایش ${data?.userInfo?.firstName} ${data?.userInfo?.lastName}`
                : "ایجاد"
            }
          />

          <CardContent>
            <Formik
              initialValues={{
                ...data?.userInfo,
                userKind: normalizeUserKindForForm(data?.userInfo?.userKind),
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const handleRoleChange = (e) => {
                  handleChange(e);
                  const kind = resolveUserKindFromRoleId(
                    e.target.value,
                    data?.roleList
                  );
                  if (kind) setFieldValue("userKind", kind);
                };
                return (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      aria-label="basic tabs example"
                    >
                      <Tab label="اطلاعات کاربری" {...a11yProps(0)} />
                      <Tab label="شهرستان" {...a11yProps(1)} />
                    </Tabs>
                  </Box>
                  {/* User Info */}
                  <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="نام"
                          variant="outlined"
                          fullWidth
                          name="firstName"
                          type="string"
                          value={values.firstName}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={errors.firstName && touched.firstName}
                          helperText={
                            errors.firstName &&
                            touched.firstName &&
                            errors.firstName
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="نام خانوادگی"
                          variant="outlined"
                          fullWidth
                          name="lastName"
                          type="lastName"
                          value={values.lastName}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={errors.lastName && touched.lastName}
                          helperText={
                            errors.lastName &&
                            touched.lastName &&
                            errors.lastName
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="نام کاربری"
                          variant="outlined"
                          fullWidth
                          name="userName"
                          type="userName"
                          value={values.userName}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={errors.userName && touched.userName}
                          helperText={
                            errors.userName &&
                            touched.userName &&
                            errors.userName
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="نوع کاربر"
                          name="userKind"
                          value={values.userKind}
                          onChange={wrapFormikHandleChange(handleChange)}
                          list={data.userKind}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="ایمیل"
                          variant="outlined"
                          fullWidth
                          name="email"
                          type="email"
                          value={values.email}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={errors.email && touched.email}
                          helperText={
                            errors.email && touched.email && errors.email
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="موبایل"
                          variant="outlined"
                          fullWidth
                          name="mobile"
                          type="mobile"
                          value={values.mobile}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={errors.mobile && touched.mobile}
                          helperText={
                            errors.mobile && touched.mobile && errors.mobile
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <RoleInfoSelect
                          name="roleId"
                          roleList={data.roleList}
                          value={values.roleId}
                          onChange={wrapFormikHandleChange(handleRoleChange)}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="رمز عبور"
                          variant="outlined"
                          fullWidth
                          name="password"
                          type="password"
                          value={values.password}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={errors.password && touched.password}
                          helperText={
                            errors.password &&
                            touched.password &&
                            errors.password
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="تکرار رمز عبور"
                          variant="outlined"
                          fullWidth
                          name="confirmPassword"
                          type="password"
                          value={values.confirmPassword}
                          onChange={wrapFormikHandleChange(handleChange)}
                          error={
                            errors.confirmPassword && touched.confirmPassword
                          }
                          helperText={
                            errors.confirmPassword &&
                            touched.confirmPassword &&
                            errors.confirmPassword
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <FormControlLabel
                          name="isActive"
                          value={values.isActive}
                          checked={values.isActive}
                          onChange={wrapFormikHandleChange(handleChange)}
                          control={<Switch />}
                          label="فعال"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>

                  {/* City permissions (hierarchical by province -> city) */}
                  <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={12}>
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={() => onHandleAllCheckedCity(true)}
                        >
                          انتخاب همه
                        </Button>

                        <Button
                          color="gray"
                          variant="outlined"
                          sx={{ mx: 2 }}
                          onClick={() => onHandleAllCheckedCity(false)}
                        >
                          حذف همه
                        </Button>
                      </Grid>

                      {/* اگر اطلاعات استان/شهر لود شده، نمایش آبشاری؛ در غیر این صورت ساختار ساده قبلی */}
                      {provinceGroups.length > 0
                        ? provinceGroups.map((province) => {
                            const total = province.cities.length;
                            const selectedCount = province.cities.filter(
                              (c) => c.allowed
                            ).length;
                            const allChecked =
                              total > 0 && selectedCount === total;
                            const someChecked =
                              selectedCount > 0 && selectedCount < total;
                            const expanded = isProvinceExpanded(
                              province.provinceId
                            );

                            return (
                              <Grid item xs={12} key={province.provinceId}>
                                <Box
                                  sx={{
                                    border: "1px solid #eee",
                                    borderRadius: 1,
                                    p: 1,
                                    mb: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={allChecked}
                                          indeterminate={someChecked}
                                          onChange={(e) =>
                                            onToggleProvinceCities(
                                              province.provinceId,
                                              e.target.checked
                                            )
                                          }
                                        />
                                      }
                                      label={province.provinceName}
                                    />

                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        toggleProvinceExpanded(
                                          province.provinceId
                                        )
                                      }
                                    >
                                      {expanded ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Box>

                                  {expanded && (
                                    <Grid
                                      container
                                      spacing={1}
                                      sx={{ pl: 3, pt: 1 }}
                                    >
                                      {province.cities.map((city) => (
                                        <Grid
                                          item
                                          xs={12}
                                          md={4}
                                          lg={3}
                                          key={city.cityId}
                                        >
                                          <FormControlLabel
                                            onChange={(e) =>
                                              onCheckedCityPermission(e, city)
                                            }
                                            control={
                                              <Checkbox
                                                checked={city.allowed}
                                              />
                                            }
                                            label={city.cityName}
                                          />
                                        </Grid>
                                      ))}
                                    </Grid>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })
                        : cityPermission.map((city) => (
                            <Grid
                              item
                              xs={12}
                              md={4}
                              lg={3}
                              key={city.cityId}
                            >
                              <FormControlLabel
                                onChange={(e) =>
                                  onCheckedCityPermission(e, city)
                                }
                                control={<Checkbox checked={city.allowed} />}
                                label={city.cityName}
                              />
                            </Grid>
                          ))}
                    </Grid>
                  </TabPanel>

                  <Grid
                    item
                    xs={12}
                    md={12}
                    container
                    justifyContent={"flex-end"}
                    spacing={3}
                  >
                    <Button
                      disabled={isSubmitting}
                      variant="outlined"
                      size="large"
                      color="gray"
                      sx={{ mx: 1 }}
                      onClick={() => {
                        navigate("/UM/Yeksa/userinfo");
                      }}
                    >
                      انصراف
                    </Button>

                    <Button
                      disabled={isSubmitting}
                      variant="contained"
                      type="submit"
                      size="large"
                    >
                      {loading && (
                        <CircularProgress size={16} color="inherit" />
                      )}
                      {userId !== "create" ? "ویرایش" : "ایجاد"}
                    </Button>
                  </Grid>
                </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UserInfoModifyPage;
